import React, {
	useContext, useState, useMemo, useCallback, useRef, useEffect,
} from 'react';
import debounce from 'lodash.debounce';
import { compareItems } from '../../utils/utils';

import { ListContext } from '../ListContext';
import { getRegexFromQuery } from '../../utils/database/regex';

export default function (WrappedComponent) {
	const ReturnedComponent = function (props) {
		const {
			filterFields,
			initialSortBy,
			initialSortDirection,
			defaultFrontEndSortFunction,
			initialFrontEndFilter,
		} = props;

		const listContext = useContext(ListContext);
		const { items } = listContext;
		const {
			extraFields,
			...otherVariables
		} = listContext?.variables || {};

		/*
		NOTE: In order to make this list completely front-end,
		we re-instantiate variables state here. The reasons are several-fold:

		(1) We never want to update the variables in withListQuery, as these
			will fire the query again.

		(2) We never want to use the second set of variables from withListQuery
			either, as these are initialised with starting back-end filters,
			which preferably we want to ignore with the next set of variables.
			(It makes for cleaner, simpler code, than to have to maintain
			those variables here too).

		*/

		const [variables, setVariables] = useState({
			...otherVariables,
			sortBy: initialSortBy,
			sortDirection: initialSortDirection,
			sortByResolver: null,
			filter: initialFrontEndFilter || {},
			...(extraFields ? { extraFields } : {}),
		});

		// The flag indicates if user changed the sort order clicking one of column headers.
		// It's used to override defaultFrontEndSortFunction
		const [isInitialSortChanged, setIsInitialSortChanged] = useState(false);

		const {
			filter,
			sortBy,
			sortDirection,
			sortByResolver,
		} = variables;

		// Use a ref to always have access to the latest filter state
		// This solves the closure problem with the debounced function
		const filterRef = useRef(filter);
		useEffect(() => {
			filterRef.current = filter;
		}, [filter]);

		const updateVariables = useCallback(newVariables => setVariables({
			...variables,
			...newVariables,
		}), [variables, setVariables]);

		const filterItems = useCallback(newFilter => {
			const mergedFilter = { ...filter, ...newFilter };

			updateVariables({ filter: mergedFilter });
		}, [filter, updateVariables]);

		const frontEndFilter = useCallback(items => {
			const regexSearchPhase = filter.query ? new RegExp(getRegexFromQuery(filter.query), 'i') : null;

			return items.filter(item => {
				// Check if item matches the search query (if one exists)
				let matchesSearchQuery = true;
				if (filter.query) {
					let searchStrings = [];

					if (!filterFields) {
						// Default: search name of item if no filter fields passed in; hack for machines
						searchStrings = [item.assetId === undefined ? item.name : item.assetId];
					}
					else {
						filterFields.forEach(fieldFunction => {
							// The field tokenized by space
							const field = fieldFunction(item);

							// Must be a string to push into search Strings array
							if (typeof field === 'string') {
								searchStrings.push(field);
							}
						});
					}

					// Check if any search word matches the query
					matchesSearchQuery = searchStrings.filter(Boolean).some(word => word.match(regexSearchPhase));
				}

				// If search query doesn't match, exclude the item
				if (!matchesSearchQuery) {
					return false;
				}

				// Now check all other (non-query) filters
				// eslint-disable-next-line no-unused-vars
				const { query, ...nonQueryFilters } = filter;

				const filterKeys = Object.keys(nonQueryFilters);

				for (const filterKey of filterKeys) {
					const filterValue = filter[filterKey];

					// E.g. if { HCGCategory: undefined }
					// then we won't filter out any items.
					// (Same as backend filter).
					if (filterValue === undefined) {
						continue;
					}

					// resolve real field if filterKey has a dot and it's a nested field
					const realFieldValue = filterKey?.split('.')?.reduce((acc, key) => acc && acc[key], item);

					const searchString = realFieldValue && realFieldValue?.toString()?.toLowerCase();

					// Check if filterValue is an array
					if (Array.isArray(filterValue)) {
						// Check if any value in the array matches the searchString
						const matchFound = filterValue.some(value => value?.toString()?.toLowerCase() === searchString);

						if (!matchFound) {
							return false;
						}
					} else {
						// Original logic for non-array values
						const filterString = filterValue && filterValue?.toString()?.toLowerCase();

						// Check for equivalancy - can be changed
						// depending on requirements
						if (filterString !== searchString) {
							return false;
						}
					}
				}

				return true;
			});
		}, [filter, filterFields]);

		const changeFilter = useCallback(newFilter => {
			return filterItems(newFilter);
		}, [filterItems]);

		// Create a debounced query filter that uses the ref to access the latest filter state
		// This ensures other filter changes are properly preserved when searching
		const changeQueryFilterDebounced = useMemo(() => {
			return debounce((newQueryString) => {
				// Use the ref to get the current filter state at execution time
				const mergedFilter = { ...filterRef.current, query: newQueryString };
				updateVariables({ filter: mergedFilter });
			}, 300);
		}, [updateVariables]);

		const changeSort = useCallback((newSortBy, newSortByResolver) => {
			let newSortDirection = 1;
			if (sortBy === newSortBy) {
				newSortDirection = -1 * sortDirection;
			}
			updateVariables({
				sortDirection: newSortDirection,
				sortBy: newSortBy,
				sortByResolver: newSortByResolver,
			});

			setIsInitialSortChanged(true);
		}, [sortBy, sortDirection, updateVariables, setIsInitialSortChanged]);

		const changeSortDirection = useCallback(newSortDirection => {
			updateVariables({
				sortDirection: newSortDirection,
			});
		}, [updateVariables]);

		// Front-end sort items in place
		const sortFunction = useMemo(
			() => ((!isInitialSortChanged && defaultFrontEndSortFunction) ? defaultFrontEndSortFunction : compareItems(sortBy, sortDirection, sortByResolver)),
			[isInitialSortChanged, defaultFrontEndSortFunction, sortBy, sortDirection, sortByResolver],
		);

		const processedItems = useMemo(() => {
			let filteredItems = !items ? items : frontEndFilter(items);
			if (filteredItems) {
				filteredItems = [...filteredItems].sort(sortFunction);
			}

			return filteredItems;
		}, [items, frontEndFilter, sortFunction]);

		const context = useMemo(() => ({
			...listContext,
			filterItems,
			changeFilter,
			changeQueryFilterDebounced,
			changeSort,
			changeSortDirection,
			items: processedItems || [],
			loadingInitial: listContext.loadingInitial || processedItems === null,
			allItems: items || [],
			hasPagination: false,

			// Over-write variables:
			variables,
		}), [listContext, filterItems, changeFilter, changeQueryFilterDebounced, changeSort, changeSortDirection, processedItems, items, variables]);

		return (
			<ListContext.Provider value={context}>
				<WrappedComponent {...props} />
			</ListContext.Provider>
		);
	};

	return ReturnedComponent;
}
